import { Injectable } from '@angular/core';
import { LoadingController, AlertController, ToastController, NavController } from '@ionic/angular';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import dayjs from 'dayjs';
import { log } from 'src/app/core/log';
@Injectable({
  providedIn: 'root'
})
export class UtilService {
  loader: any;
  isLoading = false;

  details: any;
  private events = new Subject<any>();
  private profile = new Subject<any>();
  constructor(
    public loadingCtrl: LoadingController,
    public alertCtrl: AlertController,
    private toastCtrl: ToastController,
    public router: Router,
    private navCtrl: NavController,
    private translateService: TranslateService
  ) {}
  /*
  Start Loader
  Call this method to Start your Loader
  */

  async show(title?) {
    this.isLoading = true;
    return await this.loadingCtrl
      .create({
        message: title,
        spinner: 'bubbles'
      })
      .then((a) => {
        void a.present().then(() => {
          // log('presented');
          if (!this.isLoading) {
            void a.dismiss().then(() => log('abort presenting'));
          }
        });
      });
  }

  async hide() {
    this.isLoading = false;
    // Si el spinner todavia no llego a presentarse, `dismiss()` rechaza con «overlay does
    // not exist». No hay nada que cerrar: `show()` lo cierra solo al presentarse, porque
    // `isLoading` ya es false.
    return await this.loadingCtrl
      .dismiss()
      .then(() => log('dismissed'))
      .catch((error) => log('no habia spinner que cerrar', error));
  }

  /**
   * NV-54: avisa que cambiar lo que publica la vitrina (nombre, WhatsApp o Telegram) la vuelve a
   * revisión, y pide confirmar **antes** de guardar. `true` solo si el dueño acepta.
   */
  async confirmStorefrontReview(): Promise<boolean> {
    const alert = await this.alertCtrl.create({
      header: this.translate('Your storefront goes back to review'),
      message: this.translate(
        'You changed what your storefront publishes (name, WhatsApp or Telegram). It stays hidden until Mitter reviews it again.'
      ),
      buttons: [
        { text: this.translate('Cancel'), role: 'cancel' },
        { text: this.translate('Save'), role: 'confirm' }
      ]
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'confirm';
  }

  /*
    Show Warning Alert Message
    param : msg = message to display
    Call this method to show Warning Alert,
    */
  async showWarningAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: 'Warning',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  getCurrencyCode() {
    return environment.general.code;
  }

  getCurrecySymbol() {
    return environment.general.symbol;
  }

  getObservable(): Subject<any> {
    return this.events;
  }

  publishNewAddress(data: any) {
    this.events.next(data);
  }

  publishProfile(data: any) {
    this.profile.next(data);
  }

  obserProfile(): Subject<any> {
    return this.profile;
  }

  async showSimpleAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: '',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  /**
   * Traduce al idioma activo.
   *
   * El respaldo apuntaba a `translations.en_merch[str]`, un idioma que no existe: los
   * diccionarios de este repo son `en` y `spanish`. O sea que **cualquier clave sin
   * traducir lanzaba TypeError** en vez de degradar. Y la primera linea tampoco estaba
   * a salvo: `translations[currentLang]` es `undefined` hasta que el diccionario
   * termina de cargar, o si `currentLang` quedo en un idioma sin archivo.
   *
   * Ahora el respaldo es la clave misma, que es lo que hace `TranslateService` y lo que
   * mitter-driver-app adopto al cerrar el mismo bug (#2).
   */
  translate(str) {
    return this.lookup(this.translateService.currentLang, str);
  }
  /** Igual que `translate()` pero siempre en ingles. Lo usan las push. */
  translateInEN(str) {
    return this.lookup('en', str);
  }

  /** Busca la clave en un diccionario; si no esta, devuelve la clave. */
  private lookup(lang: string, str: string): string {
    const dictionary = this.translateService.translations[lang];
    const value = dictionary ? dictionary[str] : undefined;
    return value === undefined ? str : value;
  }

  /*
   Show Error Alert Message
   param : msg = message to display
   Call this method to show Error Alert,
   */
  async showErrorAlert(msg) {
    const alert = await this.alertCtrl.create({
      header: 'Error',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

  /*
     param : email = email to verify
     Call this method to get verify email
     */
  async getEmailFilter(email) {
    const emailfilter = /^[\w._-]+[+]?[\w._-]+@[\w.-]+\.[a-zA-Z]{2,6}$/;
    if (!emailfilter.test(email)) {
      const alert = await this.alertCtrl.create({
        header: 'Warning',
        message: 'Please enter valid email',
        buttons: ['OK']
      });
      await alert.present();
      return false;
    } else {
      return true;
    }
  }

  /*
    Show Toast Message on Screen
     param : msg = message to display, color = background color
      of toast example dark,danger,light. position  = position of message example top,bottom
     Call this method to show toast message
     */

  async showToast(msg, colors, positon) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2000,
      color: colors,
      position: positon
    });
    void toast.present();
  }
  async shoNotification(msg, colors, positon) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 4000,
      color: colors,
      position: positon,
      buttons: [
        {
          text: 'Ok',
          role: 'cancel',
          handler: () => {
            // log('Cancel clicked');
          }
        }
      ]
    });
    void toast.present();
  }

  async errorToast(msg) {
    const toast = await this.toastCtrl.create({
      message: msg,
      duration: 2000,
      color: 'danger'
    });
    void toast.present();
  }

  apiErrorHandler(err) {
    // log('Error got in service =>', err)
    if (err.status === -1) {
      void this.showErrorAlert('Failed To Connect With Server');
    } else if (err.status === 401) {
      void this.showErrorAlert('Unauthorized Request!');
      void this.navCtrl.navigateRoot('/login');
    } else if (err.status === 500) {
      void this.showErrorAlert('Somethimg Went Wrong..');
    }
  }

  setDetails(data) {
    this.details = null;
    this.details = data;
  }
  getDetails() {
    return this.details;
  }
  makeid(length) {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const charactersLength = characters.length;
    for (let i = 0; i < length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  formatDate(date) {
    return dayjs(date.toDate()).format('DD/MM/YYYY HH:mm:ss');
  }
}
